import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/utils/number';
import { pcmpRequestedCountsQueryOptions } from '../../api/pcmpRequested.api';
import { usePcmpRequestedFilters } from '../../hooks/usePcmpRequestedFilters';
import { PCMP_REQUESTED_BATCHES_TAB } from '../../types/pcmpRequested.types';
import { PcmpQueueFilterBar } from '../PcmpQueue/PcmpQueueFilterBar';
import { PcmpQueueTabs } from '../PcmpQueue/PcmpQueueTabs';
import { PcmpUrgentBatchesView } from '../PcmpRequests/PcmpUrgentBatchesView';
import { PcmpRequestedListView } from './PcmpRequestedListView';

/**
 * Legacy RequestedProposals: the open requests by category, the approved ones,
 * Urgent Pre-Batch and the urgent batches. Approvers act; everyone else reads.
 */
export function PcmpRequestedQueueTable() {
  const controller = usePcmpRequestedFilters();
  const { tab, appliedFilters, listParams } = controller;

  const { data: counts } = useQuery(pcmpRequestedCountsQueryOptions(appliedFilters));

  const { tabKeys, labels, countByKey } = useMemo(() => {
    const keys = (counts?.tabs ?? []).map((entry) => entry.key);
    const tabLabels: Record<string, string> = {};
    const byKey = new Map<string, number>();
    for (const entry of counts?.tabs ?? []) {
      tabLabels[entry.key] = entry.label;
      byKey.set(entry.key, entry.count);
    }
    keys.push(PCMP_REQUESTED_BATCHES_TAB);
    tabLabels[PCMP_REQUESTED_BATCHES_TAB] = 'Batches';
    byKey.set(PCMP_REQUESTED_BATCHES_TAB, counts?.batches ?? 0);
    return { tabKeys: keys, labels: tabLabels, countByKey: byKey };
  }, [counts]);

  const pending = counts?.tabs.find((entry) => entry.kind === 'ALL')?.count;

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Requested Proposals</h1>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {counts
            ? counts.canAct
              ? `${formatNumber(pending ?? 0)} requests awaiting a decision`
              : 'Requests from you and your team. Only an Admin or MIS can decide them.'
            : ' '}
        </p>
      </div>

      {counts ? (
        <PcmpQueueTabs
          tabs={tabKeys}
          labels={labels}
          value={tab}
          countOf={(key) => countByKey.get(key) ?? 0}
          onChange={controller.setTab}
        />
      ) : (
        <Skeleton className="h-10 w-full max-w-2xl" />
      )}

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-requested" />

      {listParams === null ? (
        <PcmpUrgentBatchesView controller={controller} />
      ) : (
        <PcmpRequestedListView
          controller={controller}
          params={listParams}
          tabInfo={counts?.tabs.find((entry) => entry.key === tab)}
        />
      )}
    </div>
  );
}
